import { memo, useCallback, useEffect, useRef, useState } from "react";
import F7 from "../../../utils/F7";
import mapUtils from "../../../utils/map";
import { getListItemStyleAsHTML } from "../../inventory/components/ListItemStyle";
import { useNavigate } from "react-router-dom";
import { useMap } from "../../../App";
import { useMapClickEnabled } from "../../../state";
import { useInventoryContents } from "../../../hooks/database/useInventoryContents";

function hasPolygon(features: ol.Feature[]) {
  return features.filter((f) => f.getGeometry().getType() === "Polygon").length > 0;
}

function checkIfCoordsInsidePoint(feature: ol.Feature, x: number, y: number, pointX: number, pointY: number) {
  const distance = Math.sqrt(Math.pow(x - pointX, 2) + Math.pow(y - pointY, 2));
  let size = 1;
  if (feature && feature.get && feature.get("features")) {
    size = feature.get("features").length;
  }
  const radius = Math.max(8, Math.min(size * 0.75, 12));

  return distance <= radius;
}

const GeomSelect = memo(() => {
  const [selectedFeatures, setSelectedFeatures] = useState<ol.Collection<ol.Feature>>();
  const interactionRef = useRef<ol.interaction.Select>();

  const map = useMap()!;

  useEffect(() => {
    if (!map) {
      return () => {};
    }

    interactionRef.current = mapUtils.createSelectInteraction();
    map.addInteraction(interactionRef.current);
    setSelectedFeatures(interactionRef.current.getFeatures());
    return () => {
      map.removeInteraction(interactionRef.current!);
    };
  }, [map]);

  const { data: objects } = useInventoryContents();

  const navigate = useNavigate();
  const goToObject = useCallback((id: string | number) => navigate(`o/${id}`), [navigate]);

  const showGeomSelectActionSheet = useCallback(
    (features: ol.Feature[]) => {
      if (!objects) {
        return;
      }

      let pointX: number;
      let pointY: number;
      let geometryId = "";
      let sumPoints = 0;
      let sumOther = 0;
      const featureButtons = features.map((feature) => {
        const geomTitle = feature.get("title");
        const geomId = feature.getId();
        const currFeature = objects.find((el) => el.id === feature.getId())!;
        const color = mapUtils.getComputedStyle(mapUtils.inventoryStyle, [feature]);
        const featureType = currFeature.geom.geometry.type;
        const item = `<span>${getListItemStyleAsHTML(
          featureType,
          color
        )}<Titl style="position:relative;left:5px">${geomTitle}</Titl></span>`;
        if (featureType === "Point") {
          [pointX, pointY] = (feature.getGeometry() as ol.geom.Point).getCoordinates();
          geometryId = geomId as string;
        }
        sumPoints += featureType === "Point" ? 1 : 0;
        sumOther += featureType !== "Point" ? 1 : 0;
        return {
          text: item,
          onClick: () => goToObject(geomId),
        };
      });

      const cancelButtons = [
        {
          text: "Cancel",
          color: "red",
        },
      ];
      const resArray: boolean[] = [];
      let singlePointSelect = true;
      if (sessionStorage.getItem("singlePointSelect") && sessionStorage.getItem("singlePointSelect") === "false") {
        singlePointSelect = false;
      }
      if (sumPoints === 1 && sumOther >= 0 && singlePointSelect) {
        if (sumOther > 0) {
          features.forEach((feature) => {
            const geom = feature.getGeometry() as any;
            if (geom.B.length > 2) {
              const length = geom.B.length - 1;
              for (let i = 0; i < length; i++) {
                const x = geom.B[i];
                const y = geom.B[i + 1];
                if (i === 0 || i++ <= length) {
                  resArray.push(checkIfCoordsInsidePoint(feature, x, y, pointX, pointY));
                }
              }
            }
          });
          const coordPairsOutside = resArray.filter((el) => !el);
          const coordPairsInside = resArray.filter((el) => el);
          if (coordPairsInside.length >= 2 && coordPairsOutside.length >= 2) {
            goToObject(geometryId);
          } else if (coordPairsInside.length >= 2) {
            F7.actions([featureButtons, cancelButtons]);
          } else if (coordPairsOutside.length >= 1) {
            goToObject(geometryId);
          } else {
            F7.actions([featureButtons, cancelButtons]);
          }
        } else if (sumOther === 0) {
          goToObject(geometryId);
        }
      } else {
        F7.actions([featureButtons, cancelButtons]);
      }
    },
    [objects, goToObject]
  );

  const loadFirstFeature = useCallback(
    (features: ol.Feature[]) => {
      const selectedFeature = features[0];
      let geomId = selectedFeature.getId();
      if (!geomId) {
        geomId = selectedFeature.get("features")[0].getId();
      }
      goToObject(geomId);
    },
    [goToObject]
  );

  const clearSelection = useCallback(() => {
    if (selectedFeatures) {
      selectedFeatures.clear();
    }
  }, [selectedFeatures]);

  const getFeaturesFromCluster = useCallback((clusteredAndNotClusterFeatures: ol.Feature[]) => {
    return clusteredAndNotClusterFeatures.reduce<ol.Feature[]>((result, feature) => {
      if (feature.getProperties() && feature.get("features") && feature.get("features").length) {
        return [...result, ...feature.get("features")];
      } else if (feature) {
        return [...result, feature];
      } else {
        return result;
      }
    }, []);
  }, []);

  const handleMapClick = useCallback(
    (e: ol.MapBrowserPointerEvent) => {
      clearSelection();
      const features = mapUtils.getFeaturesFromPixel(map, e.pixel);

      if (
        (features && features[0] && features[0].getKeys().includes("features") && features[0].get("features").length > 1) ||
        features.length > 1
      ) {
        const resolution = mapUtils.getResolution(map);
        const minResolutionBeforeShowingActionSheet = {
          point: 1,
          polygon: 2,
        };
        if (hasPolygon(features) && resolution < minResolutionBeforeShowingActionSheet.polygon) {
          showGeomSelectActionSheet(getFeaturesFromCluster(features));
        } else if (resolution > minResolutionBeforeShowingActionSheet.point) {
          mapUtils.panAndZoom(map, e.coordinate);
        } else {
          showGeomSelectActionSheet(getFeaturesFromCluster(features));
        }
      } else if (features.length === 1) {
        loadFirstFeature(features);
      }
    },
    [clearSelection, map, showGeomSelectActionSheet, getFeaturesFromCluster, loadFirstFeature]
  );

  const mapClickEnabled = useMapClickEnabled();
  useEffect(() => {
    if (!map) {
      return;
    }
    if (mapClickEnabled) {
      map.on("click", handleMapClick);
    } else {
      map.un("click", handleMapClick);
    }
    return () => map.un("click", handleMapClick);
  }, [mapClickEnabled, handleMapClick, map]);

  return null;
});

export default GeomSelect;
